import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { getLogLevelTone } from '@shared/config/theme';
import type { Tone } from '@shared/config/theme';
import { toneChipClasses } from '@shared/utils/statusTone';

interface AgentCommunicationLog {
  id: string;
  organization_id: string;
  agent_id: string;
  level: string;
  source: string;
  message: string;
  metadata?: any;
  created_at: string;
}

interface AgentCommunicationLogsResponse {
  logs: AgentCommunicationLog[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

interface AgentCommunicationLogsViewerProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  agentName: string;
}

export default function AgentCommunicationLogsViewer({
  isOpen,
  onClose,
  agentId,
  agentName,
}: AgentCommunicationLogsViewerProps) {
  const { t, i18n } = useTranslation('agents');
  const [logs, setLogs] = useState<AgentCommunicationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sources, setSources] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus();

    return () => previousFocus?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && agentId) {
      fetchLogs();
      fetchSources();
    }
  }, [isOpen, agentId, page, sourceFilter, levelFilter, startDate, endDate]);

  // Helper to format date for API (ensures local time is preserved)
  const formatDateForAPI = (dateString: string, isEndOfDay: boolean): string => {
    if (!dateString) return '';

    // dateString is in format "YYYY-MM-DDTHH:mm" from datetime-local input
    // Append seconds and timezone offset to preserve local time
    const suffix = isEndOfDay ? ':59.999' : ':00.000';
    const localDateTime = dateString + suffix;

    // Create date from local string and convert to ISO format with timezone
    const date = new Date(localDateTime);
    return date.toISOString();
  };

  const fetchLogs = () => {
    setLoading(true);
    const token = getToken();

    let url = `/api/v1/logs/agent-communication?agent_id=${agentId}&page=${page}&limit=50`;

    if (sourceFilter) {
      url += `&source=${sourceFilter}`;
    }

    if (levelFilter) {
      url += `&level=${levelFilter}`;
    }

    if (startDate) {
      const formattedStart = formatDateForAPI(startDate, false);
      url += `&start_date=${encodeURIComponent(formattedStart)}`;
    }

    if (endDate) {
      const formattedEnd = formatDateForAPI(endDate, true);
      url += `&end_date=${encodeURIComponent(formattedEnd)}`;
    }

    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch agent communication logs');
        }
        return res.json();
      })
      .then((data: AgentCommunicationLogsResponse) => {
        setLogs(data.logs);
        setTotalCount(data.total);
        setTotalPages(data.total_pages);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching agent communication logs:', error);
        setLoading(false);
      });
  };

  const fetchSources = () => {
    const token = getToken();

    fetch('/api/v1/logs/agent-communication/sources', {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch sources');
        }
        return res.json();
      })
      .then((data: { sources: string[] }) => {
        setSources(data.sources);
      })
      .catch((error) => {
        console.error('Error fetching sources:', error);
      });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(i18n.resolvedLanguage);
  };

  // Helper to decode metadata if it's Base64 encoded
  const decodeMetadata = (metadata: any): any => {
    if (typeof metadata === 'string') {
      try {
        // Try to decode as Base64
        const decoded = atob(metadata);
        // Try to parse as JSON
        return JSON.parse(decoded);
      } catch {
        // Not Base64 or not valid JSON, return as-is
        return metadata;
      }
    }
    return metadata;
  };

  // Helper to format metadata for display
  const formatMetadata = (metadata: any): string => {
    const decoded = decodeMetadata(metadata);
    return JSON.stringify(decoded, null, 2);
  };

  const getLevelBadge = (level: string) => (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${toneChipClasses[getLogLevelTone(level)]}`}
    >
      {level}
    </span>
  );

  const getSourceBadge = (source: string) => {
    // Categorical, not a state: each log source keeps a stable hue.
    const sourceTones: Record<string, Tone> = {
      'agent.heartbeat': 'ok',
      'agent.register': 'info',
      'agent.command': 'warn',
    };
    const tone = sourceTones[source] ?? 'neutral';

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${toneChipClasses[tone]}`}
      >
        {source}
      </span>
    );
  };

  const filteredLogs = logs.filter(
    (log) =>
      log.message.toLowerCase().includes(filter.toLowerCase()) ||
      log.source.toLowerCase().includes(filter.toLowerCase())
  );

  const handleDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (focusable.length === 0) {
      event.preventDefault();
      event.currentTarget.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="agent-communication-logs-title"
      tabIndex={-1}
      onKeyDown={handleDialogKeyDown}
    >
      <div className="flex min-h-full items-start justify-center px-4 py-4 text-center sm:items-center sm:py-8">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-slate-900/70 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        ></div>

        {/* Modal panel */}
        <div className="relative z-10 flex max-h-[calc(100dvh-2rem)] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white text-left shadow-xl dark:bg-surface-card-dark">
          {/* Header */}
          <div className="bg-white dark:bg-surface-card-dark px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-surface-border-light dark:border-surface-border-dark">
            <div className="flex items-center justify-between">
              <div>
                <h3
                  id="agent-communication-logs-title"
                  className="text-lg leading-6 font-medium text-slate-900 dark:text-slate-100"
                >
                  {t('communicationLogs.title')}
                </h3>
                <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark">
                  {agentName} • {t('communicationLogs.totalCount', { count: totalCount })}
                </p>
              </div>
              <button
                ref={closeButtonRef}
                onClick={onClose}
                aria-label={t('communicationLogs.close')}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-text-subtle-light hover:bg-surface-2-light dark:text-text-subtle-dark dark:hover:bg-surface-2-dark focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-surface-2-light dark:bg-surface-2-dark px-4 py-3 sm:px-6 border-b border-surface-border-light dark:border-surface-border-dark">
            <div className="flex flex-col gap-3">
              {/* Row 1: Search and Source */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-[minmax(0,1fr)_12rem_12rem]">
                <div className="sm:col-span-2 md:col-span-1">
                  <input
                    type="text"
                    aria-label={t('communicationLogs.search')}
                    placeholder={t('communicationLogs.search')}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="app-field-control min-h-11 w-full"
                  />
                </div>
                <div className="min-w-0">
                  <select
                    aria-label={t('communicationLogs.source')}
                    value={sourceFilter}
                    onChange={(e) => {
                      setSourceFilter(e.target.value);
                      setPage(1);
                    }}
                    className="app-field-control min-h-11 w-full"
                  >
                    <option value="">{t('communicationLogs.allSources')}</option>
                    {sources.map((source) => (
                      <option key={source} value={source}>
                        {source}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <select
                    aria-label={t('communicationLogs.level')}
                    value={levelFilter}
                    onChange={(e) => {
                      setLevelFilter(e.target.value);
                      setPage(1);
                    }}
                    className="app-field-control min-h-11 w-full"
                  >
                    <option value="">{t('communicationLogs.allLevels')}</option>
                    <option value="INFO">INFO</option>
                    <option value="WARN">WARN</option>
                    <option value="ERROR">ERROR</option>
                    <option value="DEBUG">DEBUG</option>
                  </select>
                </div>
              </div>
              {/* Row 2: Date Range */}
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <label
                      htmlFor="agent-log-start-date"
                      className="text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap"
                    >
                      {t('communicationLogs.from')}
                    </label>
                    <input
                      type="datetime-local"
                      id="agent-log-start-date"
                      aria-label={t('communicationLogs.from')}
                      value={startDate}
                      onChange={(e) => {
                        let value = e.target.value;
                        // If user selected a date without time, default to 00:00
                        if (value && value.length === 10) {
                          value = value + 'T00:00';
                        }
                        setStartDate(value);
                        setPage(1);
                      }}
                      className="app-field-control min-h-11 min-w-0 flex-1"
                    />
                  </div>
                  <div className="flex min-w-0 items-center gap-2">
                    <label
                      htmlFor="agent-log-end-date"
                      className="text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap"
                    >
                      {t('communicationLogs.to')}
                    </label>
                    <input
                      type="datetime-local"
                      id="agent-log-end-date"
                      aria-label={t('communicationLogs.to')}
                      value={endDate}
                      onChange={(e) => {
                        let value = e.target.value;
                        // If user selected a date without time, default to 23:59
                        if (value && value.length === 10) {
                          value = value + 'T23:59';
                        }
                        setEndDate(value);
                        setPage(1);
                      }}
                      className="app-field-control min-h-11 min-w-0 flex-1"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      // Last 24 hours
                      const now = new Date();
                      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                      const formatDT = (d: Date) => {
                        const yyyy = d.getFullYear();
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        const dd = String(d.getDate()).padStart(2, '0');
                        const hh = String(d.getHours()).padStart(2, '0');
                        const min = String(d.getMinutes()).padStart(2, '0');
                        return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
                      };
                      setStartDate(formatDT(yesterday));
                      setEndDate(formatDT(now));
                      setPage(1);
                    }}
                    className="min-h-11 whitespace-nowrap rounded-lg px-3 text-brand-600 dark:text-brand-400 hover:bg-surface-card-light dark:hover:bg-surface-card-dark text-sm font-medium"
                  >
                    {t('communicationLogs.last24Hours')}
                  </button>
                  <button
                    onClick={() => {
                      // Set today: from 00:00 to 23:59
                      const today = new Date();
                      const yyyy = today.getFullYear();
                      const mm = String(today.getMonth() + 1).padStart(2, '0');
                      const dd = String(today.getDate()).padStart(2, '0');
                      setStartDate(`${yyyy}-${mm}-${dd}T00:00`);
                      setEndDate(`${yyyy}-${mm}-${dd}T23:59`);
                      setPage(1);
                    }}
                    className="min-h-11 whitespace-nowrap rounded-lg px-3 text-brand-600 dark:text-brand-400 hover:bg-surface-card-light dark:hover:bg-surface-card-dark text-sm font-medium"
                  >
                    {t('communicationLogs.today')}
                  </button>
                  <button
                    onClick={() => {
                      setStartDate('');
                      setEndDate('');
                      setPage(1);
                    }}
                    className="min-h-11 whitespace-nowrap rounded-lg px-3 text-slate-600 dark:text-slate-300 hover:bg-surface-card-light dark:hover:bg-surface-card-dark text-sm"
                  >
                    {t('communicationLogs.clearDates')}
                  </button>
                  <button
                    onClick={fetchLogs}
                    className="min-h-11 whitespace-nowrap rounded-lg bg-brand-600 px-4 text-white hover:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    {t('communicationLogs.refresh')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Logs table */}
          <div className="min-h-0 flex-1 overflow-y-auto bg-white px-4 py-5 dark:bg-surface-card-dark sm:p-6">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-center py-8">
                <svg
                  className="mx-auto h-12 w-12 text-text-subtle-light dark:text-text-subtle-dark"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {t('communicationLogs.noLogsFound')}
                </h3>
                <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark">
                  {t('communicationLogs.noLogsMatch')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-surface-border-light dark:divide-surface-border-dark">
                  <thead className="bg-surface-2-light dark:bg-surface-2-dark">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                        {t('communicationLogs.timestamp')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                        {t('communicationLogs.level')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                        {t('communicationLogs.source')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                        {t('communicationLogs.message')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-surface-card-dark divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    {filteredLogs.map((log) => (
                      <tr
                        key={log.id}
                        className="hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                      >
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">{getLevelBadge(log.level)}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {getSourceBadge(log.source)}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-900 dark:text-slate-100">
                          <div>{log.message}</div>
                          {log.metadata && (
                            <details className="mt-1">
                              <summary className="cursor-pointer text-xs text-text-muted-light dark:text-text-muted-dark hover:text-slate-900 dark:hover:text-white">
                                {t('communicationLogs.viewMetadata')}
                              </summary>
                              <pre className="mt-2 text-xs bg-surface-2-light dark:bg-surface-2-dark p-2 rounded overflow-x-auto">
                                {formatMetadata(log.metadata)}
                              </pre>
                            </details>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="shrink-0 bg-surface-2-light px-4 py-3 dark:bg-surface-2-dark sm:px-6 border-t border-surface-border-light dark:border-surface-border-dark">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-700 dark:text-slate-300">
                  {t('communicationLogs.page', { page, totalPages, count: totalCount })}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    aria-label={t('communicationLogs.previous')}
                    className="min-h-11 rounded-lg border border-border-strong-light bg-surface-card-light px-3 text-sm font-medium text-slate-700 hover:bg-surface-2-light disabled:cursor-not-allowed disabled:opacity-50 dark:border-border-strong-dark dark:bg-surface-card-dark dark:text-slate-300 dark:hover:bg-surface-2-dark"
                  >
                    {t('communicationLogs.previous')}
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    aria-label={t('communicationLogs.next')}
                    className="min-h-11 rounded-lg border border-border-strong-light bg-surface-card-light px-3 text-sm font-medium text-slate-700 hover:bg-surface-2-light disabled:cursor-not-allowed disabled:opacity-50 dark:border-border-strong-dark dark:bg-surface-card-dark dark:text-slate-300 dark:hover:bg-surface-2-dark"
                  >
                    {t('communicationLogs.next')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
